const UINT64_MASK = 0xffff_ffff_ffff_ffffn;
const SPLITMIX64_INCREMENT = 0x9e37_79b9_7f4a_7c15n;
const FNV1A64_OFFSET = 0xcbf2_9ce4_8422_2325n;
const FNV1A64_PRIME = 0x0000_0100_0000_01b3n;
const TWO_TO_53 = 9_007_199_254_740_992;

export interface AiSeedInput {
  readonly gameSeed: string;
  readonly tierVersion: string;
  readonly engineBuild: string;
  readonly positionFen: string;
  readonly positionHash: string;
  readonly ply: number;
}

export interface DeterministicRandomStep {
  readonly state: bigint;
  readonly value: bigint;
}

/**
 * Derives a reproducible 64-bit seed from replay metadata and a position.
 * Length-prefixed fields prevent distinct inputs from being concatenated alike.
 */
export function deriveAiSeed(input: AiSeedInput): bigint {
  const serializedInput = [
    'capychess-ai-seed-v1',
    input.gameSeed,
    input.tierVersion,
    input.engineBuild,
    input.positionFen,
    input.positionHash,
    String(input.ply)
  ]
    .map((field) => `${field.length}:${field}`)
    .join('|');

  return mix64(fnv1a64(serializedInput));
}

export function formatAiSeed(seed: bigint): string {
  return (seed & UINT64_MASK).toString(16).padStart(16, '0');
}

/**
 * Returns the next SplitMix64 value without mutating caller-owned state.
 */
export function nextDeterministicRandom(state: bigint): DeterministicRandomStep {
  const nextState = (state + SPLITMIX64_INCREMENT) & UINT64_MASK;

  return {
    state: nextState,
    value: mix64(nextState)
  };
}

export function randomUnitInterval(value: bigint): number {
  return Number((value & UINT64_MASK) >> 11n) / TWO_TO_53;
}

function fnv1a64(value: string): bigint {
  let hash = FNV1A64_OFFSET;

  for (const byte of new TextEncoder().encode(value)) {
    hash ^= BigInt(byte);
    hash = (hash * FNV1A64_PRIME) & UINT64_MASK;
  }

  return hash;
}

function mix64(value: bigint): bigint {
  let mixed = (value + SPLITMIX64_INCREMENT) & UINT64_MASK;
  mixed = ((mixed ^ (mixed >> 30n)) * 0xbf58_476d_1ce4_e5b9n) & UINT64_MASK;
  mixed = ((mixed ^ (mixed >> 27n)) * 0x94d0_49bb_1331_11ebn) & UINT64_MASK;
  return (mixed ^ (mixed >> 31n)) & UINT64_MASK;
}
